'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

import {
    AutoComplete,
    AutoCompleteChangeEvent,
    AutoCompleteCompleteEvent
} from 'primereact/autocomplete';
import {
    Tooltip,
    TooltipPassThroughOptions
} from 'primereact/tooltip';
import TooltipOptions from 'primereact/tooltip/tooltipoptions';
import {DomHandler} from 'primereact/utils';
import PrimeReact from 'primereact/api';

import {__} from '~/shared/src/utilities/messageCatalog';
import {BaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {
    LoadAutoCompleteSuggestionsCommonParameters,
    LoadAutoCompleteSuggestionsParameters,
    LoadAutoCompleteSuggestionsResult,
    LoadAutoCompleteSuggestionsTypeDependingParameters
} from '~/shared/src/utilities/modules/data';
import {
    Alignment,
    InfoTextLayout,
    LabelPosition,
    Visibility
} from '~/shared/src/utilities/modules/form';

import xhr from '~/frontend/src/utilities/xhr';
import {StoreState} from '~/frontend/src/store';
import {markFilterSearchesInText} from '~/frontend/src/utilities/renderHelpers';
import {createSyntheticEvent} from '~/frontend/src/utilities/syntheticEvent';
import {isInvalidHTML} from '~/frontend/src/utilities/valueRenderers';
import {ResolverParameters} from '~/shared/src/utilities/messageUtils';


export type MessageResolver = ( messageKey : string, rawMode? : boolean ) => string | undefined;

export type DefaultProps = {

    /**
     * The alignment of the content of the field.
     */
    alignment? : Alignment;

    appendToElementId? : string;

    /**
     * Enable auto complete functionality.
     */
    autoComplete? : boolean;

    /**
     * The time in milliseconds to delay requesting new results for auto complete.
     */
    autoCompleteDelay? : number;

    /**
     * The minimal count of chars to be entered into the field before requesting results for auto complete.
     */
    autoCompleteMinLength? : number;

    /**
     * The CSS class name(s) to add to the wrapper.
     */
    classNames? : string[];

    /**
     * Is he field disabled?
     */
    disabled? : boolean;

    /**
     * The CSS class names to style the displayValue with.
     */
    displayClassNames? : string[];

    /**
     * The styles to style the displayValue with.
     */
    displayStyle? : React.CSSProperties;

    /**
     * The offset in rem for the displayValue with.
     */
    displayValueWidthOffsetRem? : number;

    /**
     * The offset in px for the displayValue with.
     */
    displayValueWidthOffsetPx? : number;

    /**
     * The error message to display for the field
     */
    errorMessage? : React.ReactElement | React.ReactElement[] | string | string[];

    /**
     * The help text to display for the field
     */
    helpText? : React.ReactElement | React.ReactElement[] | string | string[];

    /**
     * The layout for help texts and error messages.
     */
    infoTextLayout? : InfoTextLayout;

    /**
     * Should the field marked as dirty.
     */
    isDirty? : boolean;

    /**
     * The label for the field.
     */
    label? : string;

    /**
     * The alignment of the label for the field.
     */
    labelAlignment? : Alignment;

    /**
     * The CSS class names to style the label with.
     */
    labelClassNames? : string[];

    /**
     * The position of the label for the field relative to the field.
     */
    labelPosition? : LabelPosition;

    /**
     * The styles to style the label with.
     */
    labelStyle? : React.CSSProperties;

    /**
     * The visibility of the label for the field.
     */
    labelVisibility? : Visibility;

    /**
     * The width of the label for the field.
     */
    labelWidth? : number;

    /**
     * The CSS class names to style the help text or error message with.
     */
    messageClassNames? : string[];

    /**
     * The styles to style the help text or error message with.
     */
    messageStyle? : React.CSSProperties;

    /**
     * Is a value required for the field?
     */
    required? : boolean;

    tooltipDisabled? : boolean;

    /**
     * The styles to style the wrapper with.
     */
    style? : React.CSSProperties;

    /**
     * The visibility of the field
     */
    visibility? : Visibility;
};
export const defaultProps : Required<DefaultProps> = {
    alignment                 : 'left',
    appendToElementId         : 'self',
    autoComplete              : true,
    autoCompleteDelay         : 300,
    autoCompleteMinLength     : 1,
    classNames                : [ 'yn-field-wrapper', 'field' ],
    disabled                  : false,
    displayClassNames         : [ 'yn-field-displayvalue' ],
    displayStyle              : {},
    displayValueWidthOffsetPx : 0,
    displayValueWidthOffsetRem: 0,
    errorMessage              : '',
    helpText                  : '',
    infoTextLayout            : 'icon',
    isDirty                   : false,
    label                     : '',
    labelAlignment            : 'left',
    labelClassNames           : [ 'yn-field-label' ],
    labelPosition             : 'left',
    labelStyle                : {},
    labelVisibility           : 'visible',
    labelWidth                : 120,
    messageClassNames         : [],
    messageStyle              : {},
    required                  : false,
    tooltipDisabled           : false,
    style                     : {},
    visibility                : 'visible'
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * Has the input the focus?
     */
    autoCompleteInputHasFocus? : boolean;

    autoCompletePanelStyle? : React.CSSProperties;

    /**
     * The dataDescriptor for the auto complete suggestions to request.
     */
    autoCompleteRequestParameters? : Pick<LoadAutoCompleteSuggestionsCommonParameters, 'schema' | 'id'> & Omit<LoadAutoCompleteSuggestionsTypeDependingParameters, 'parameters'> & {
        parameters? : string[],
        additionalParameters? : { [ parameter : string ] : string; }
    };

    /**
     * Callback to format the loaded auto complete suggestions.
     * @param {string} suggestion
     */
    autoCompleteSuggestionsItemTemplate? : ( suggestion : string, searchString : string ) => React.ReactNode;

    /**
     * The value for the auto complete.
     */
    autoCompleteValue? : string;

    /**
     * The props for auto complete.
     */
    onAutoCompleteValueChange? : ( value : string, originalEvent : any ) => void;

    /**
     * The value to display instead of the real one.
     */
    displayValue? : React.ReactNode;

    /**
     * The tooltip for the wrapped field.
     */
    tooltip? : React.ReactNode;

    /**
     * The inheritance tooltip for the wrapped field.
     */
    inheritanceTooltip? : string;

    /**
     * The options for the tooltip.
     */
    tooltipOptions? : TooltipOptions.TooltipOptions;

    /**
     * The placeholders for messages translated by MessageCatalog.
     */
    messagePlaceholders? : ResolverParameters;

    /**
     * A resolver function for message templateStrings.
     *
     * @param templateString
     */
    messageTemplateStringResolver? : MessageResolver;

    pt? : {
        root? : React.HTMLAttributes<HTMLDivElement> & { [ key : `data-${string}` ] : string };
        label? : React.HTMLAttributes<HTMLLabelElement> & { [ key : `data-${string}` ] : string };
        displayValue? : React.HTMLAttributes<HTMLDivElement> & { [ key : `data-${string}` ] : string };
        infoText? : React.HTMLAttributes<HTMLElement> & { [ key : `data-${string}` ] : string };
        infoTextTooltip? : TooltipPassThroughOptions;
        tooltip? : TooltipPassThroughOptions;
    };

    children? : React.ReactNode;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;

const FieldWrapper = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const {
                  uid,
                  alignment,
                  appendToElementId,
                  autoComplete,
                  autoCompleteDelay,
                  autoCompleteInputHasFocus,
                  autoCompleteMinLength,
                  autoCompletePanelStyle,
                  autoCompleteRequestParameters,
                  autoCompleteSuggestionsItemTemplate,
                  autoCompleteValue,
                  children,
                  classNames,
                  disabled,
                  displayClassNames,
                  displayStyle,
                  displayValue,
                  displayValueWidthOffsetPx,
                  displayValueWidthOffsetRem,
                  errorMessage,
                  helpText,
                  infoTextLayout,
                  inheritanceTooltip,
                  isDirty,
                  label,
                  labelAlignment,
                  labelClassNames,
                  labelPosition,
                  labelStyle,
                  labelVisibility,
                  labelWidth,
                  messageClassNames,
                  messagePlaceholders,
                  messageStyle,
                  messageTemplateStringResolver,
                  onAutoCompleteValueChange,
                  required,
                  style,
                  tooltip,
                  tooltipOptions,
                  tooltipDisabled,
                  visibility,
                  pt
              }                                                         = props,
              autoCompleteRequestUid                                    = 'loadAutoCompleteSuggestions',
              user                                                      = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
              messageCatalogTimestamp                                   = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
              autoCompleteRef                                           = useRef<AutoComplete>( null ),
              infoTextTooltipElement                                    = useRef<Tooltip>( null ),
              tooltipElement                                            = useRef<Tooltip>( null ),
              relayedKeyboardEventTimeStamp                             = useRef<number>( 0 ),
              [ searchString, setSearchString ]                         = useState<string>( '' ),
              [ suggestions, setSuggestions ]                           = useState<React.ReactNode[]>( [] ),
              [ appendTo, setAppendTo ]                                 = useState<'self' | HTMLElement>( 'self' ),
              [ overlay, setOverlay ]                                   = useState<boolean>( false ),
              [ appliedAutoCompleteValue, setAppliedAutoCompleteValue ] = useState<string>( '' ),
              [ tooltipActive, setTooltipActive ]                       = useState<boolean>( false ),
              language                                                  = useMemo(
                  () => {
                      return user?.language ?? '';
                  },
                  [
                      user
                  ]
              ),
              messageResolver                                           = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) => messageKey === '' ? undefined : parse( __( messageKey, messagePlaceholders ) );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : parse( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '' );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedLabel                                             = useMemo(
                  () => {
                      return label ? messageResolver( label ) : undefined;
                  },
                  [
                      JSON.stringify( label ),
                      language,
                      messageCatalogTimestamp,
                      messageResolver
                  ]
              ),
              resolvedTooltip                                           = useMemo(
                  () => {
                      return typeof tooltip === 'string' ? messageResolver( tooltip ) : tooltip;
                  },
                  [
                      language,
                      messageCatalogTimestamp,
                      messageResolver,
                      JSON.stringify( tooltip )
                  ]
              ),
              resolvedInheritanceTooltip : React.ReactNode | undefined  = useMemo(
                  () => {
                      if ( inheritanceTooltip && !isInvalidHTML( inheritanceTooltip ) ) {
                          return parse( inheritanceTooltip ) as React.ReactNode;
                      }
                      else {
                          return inheritanceTooltip;
                      }
                  },
                  [
                      inheritanceTooltip
                  ]
              ),
              styleToApply                                              = { ...defaultProps.style, ...style },
              labelClassNamesToApply                                    = defaultProps.labelClassNames.concat( labelClassNames ),
              onKeyDown                                                 = useCallback(
                  ( event : React.KeyboardEvent<HTMLDivElement> ) => {
                      if ( relayedKeyboardEventTimeStamp.current !== event.timeStamp ) {
                          const input = autoCompleteRef.current?.getInput();

                          if ( input && event.target !== input ) {
                              const ke = new KeyboardEvent( 'keydown', {
                                  bubbles   : event.bubbles,
                                  cancelable: event.cancelable,

                                  altKey  : event.altKey,
                                  charCode: event.charCode,
                                  code    : event.code,
                                  ctrlKey : event.ctrlKey,
                                  key     : event.key,
                                  keyCode : event.keyCode,
                                  metaKey : event.metaKey,
                                  shiftKey: event.shiftKey,
                                  which   : event.which
                              } );

                              relayedKeyboardEventTimeStamp.current = ke.timeStamp;

                              input.dispatchEvent( ke );
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onMouseDown                                               = useCallback(
                  ( event : React.MouseEvent<HTMLDivElement> ) => {
                      const overlayPanelDomNode = autoCompleteRef.current?.getOverlay();

                      if (
                          event.target
                          && overlayPanelDomNode
                          // within overlay
                          && (
                              overlayPanelDomNode.isSameNode( event.target as Node )
                              || overlayPanelDomNode.contains( event.target as Node )
                          )
                      ) {
                          event.preventDefault();
                          event.stopPropagation();
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onContextMenu                                             = useCallback(
                  () => {
                      console.log( 'onContextMenu'.asLogLine( 100 ) );
                      // TODO: implement contextMenu with revert option like ItemFinder/detail/PropertiesPanel/PropertiesDataTable
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              autoCompleteItemTemplate                                  = ( suggestion : string ) => {
                  let retVal : React.ReactNode;

                  if ( isInvalidHTML( suggestion ) ) {
                      suggestion = suggestion.replaceAll( '<', '&lt;' ).replaceAll( '>', '&gt;' );
                  }

                  if ( autoCompleteSuggestionsItemTemplate ) {
                      retVal = autoCompleteSuggestionsItemTemplate( suggestion, searchString );
                  }
                  else {
                      retVal = suggestion;
                  }

                  if ( typeof retVal === 'string' ) {
                      retVal = parse( markFilterSearchesInText( retVal, searchString, true ) );
                  }

                  return retVal;
              },
              resolvedAutoCompleteQueryParameters                       = useMemo(
                  () => {
                      let retVal : ( Pick<LoadAutoCompleteSuggestionsCommonParameters, 'schema' | 'id'> & LoadAutoCompleteSuggestionsTypeDependingParameters ) | undefined;

                      if ( autoCompleteRequestParameters ) {
                          const autoCompleteRequestParametersCopy = {
                              ...autoCompleteRequestParameters
                          };

                          delete autoCompleteRequestParametersCopy.parameters;
                          delete autoCompleteRequestParametersCopy.additionalParameters;

                          retVal = {
                              ...autoCompleteRequestParametersCopy
                          } as Pick<LoadAutoCompleteSuggestionsCommonParameters, 'schema' | 'id'> & LoadAutoCompleteSuggestionsTypeDependingParameters;

                          if ( autoCompleteRequestParametersCopy.type === 'query' ) {
                              const additionalQueryParametersKeys = Object.keys( autoCompleteRequestParameters.additionalParameters ?? {} );

                              retVal.parameters = {};

                              if ( autoCompleteRequestParameters.parameters?.length && messageTemplateStringResolver ) {
                                  for ( const parameter of autoCompleteRequestParameters.parameters ) {
                                      const parameterTemplate = `\$\{${parameter}\}`,
                                            parameterValue    = messageTemplateStringResolver( parameterTemplate, true );

                                      if ( parameterValue && parameterValue !== parameterTemplate ) {
                                          retVal.parameters[ parameter ] = parameterValue;
                                      }
                                      else if ( autoCompleteRequestParameters.additionalParameters && additionalQueryParametersKeys.includes( parameter ) ) {
                                          retVal.parameters[ parameter ] = autoCompleteRequestParameters.additionalParameters[ parameter ];
                                      }
                                  }
                              }
                          }
                      }

                      return retVal;
                  },
                  [
                      autoCompleteRequestParameters,
                      messageTemplateStringResolver
                  ]
              ),
              onAutoCompleteChange                                      = ( event : AutoCompleteChangeEvent ) => {
                  setAppliedAutoCompleteValue( event.value );
                  onAutoCompleteValueChange?.( event.value, event );
              },
              onAutoCompleteShow                                        = () => {
                  setOverlay( true );
              },
              onAutoCompleteHide                                        = () => {
                  setOverlay( false );
              },
              onAutoCompleteSearch                                      = ( event : AutoCompleteCompleteEvent ) => {
                  if ( resolvedAutoCompleteQueryParameters ) {
                      const params : BaseRequestParameters & LoadAutoCompleteSuggestionsParameters = {
                          ...resolvedAutoCompleteQueryParameters,
                          searchString: event.query !== 'null' ? event.query : ''
                      };

                      // abort running load
                      xhr.abort( uid, autoCompleteRequestUid );

                      const doLoad = async () => {
                          const response = await xhr.post(
                              `${BASE_URL}/api/data/loadAutoCompleteSuggestions`,
                              params,
                              undefined,
                              uid,
                              autoCompleteRequestUid
                          );

                          if ( response?.data ) {
                              const responseData = response.data as LoadAutoCompleteSuggestionsResult;

                              if ( responseData.success ) {
                                  setSearchString( event.query );
                                  setSuggestions( responseData.data.suggestions );
                              }
                          }
                      };

                      doLoad().catch( ( error ) => {
                          console.log( error );
                      } );
                  }
              },
              autoCompleteComp                                          = useMemo(
                  () => {
                      let retVal : JSX.Element | undefined;

                      if ( autoComplete && autoCompleteInputHasFocus && !disabled ) {
                          retVal = <AutoComplete
                              id={`${uid}_autocomplete`}

                              ref={autoCompleteRef}

                              value={appliedAutoCompleteValue}

                              suggestions={suggestions}
                              itemTemplate={autoCompleteItemTemplate}

                              onChange={onAutoCompleteChange}
                              onShow={onAutoCompleteShow}
                              onHide={onAutoCompleteHide}
                              completeMethod={onAutoCompleteSearch}

                              appendTo={appendTo}
                              delay={autoCompleteDelay}
                              minLength={autoCompleteMinLength}
                              panelStyle={autoCompletePanelStyle}
                          />;
                      }

                      return retVal;
                  },
                  [
                      appendTo,
                      appliedAutoCompleteValue,
                      autoComplete,
                      autoCompleteDelay,
                      autoCompleteInputHasFocus,
                      autoCompleteMinLength,
                      disabled,
                      language,
                      messageCatalogTimestamp,
                      onAutoCompleteChange,
                      suggestions
                  ]
              ),
              infoTextMessage                                           = useMemo(
                  () => {
                      let retVal : React.ReactNode[] = [];

                      if ( infoTextLayout === 'text' ) {
                          if ( errorMessage ) {
                              if ( typeof errorMessage === 'string' ) {
                                  retVal = [ __( errorMessage ) ];
                              }
                              else if ( !Array.isArray( errorMessage ) ) {
                                  retVal = [ errorMessage ];
                              }
                              else {
                                  retVal = errorMessage;
                              }
                          }
                          else if ( helpText ) {
                              if ( typeof helpText === 'string' ) {
                                  retVal = [ messageResolver( helpText ) ];
                              }
                              else if ( !Array.isArray( helpText ) ) {
                                  retVal = [ helpText ];
                              }
                              else {
                                  retVal = helpText;
                              }
                          }

                          // remove linebreaks
                          if ( retVal ) {
                              for ( let i = 0; i < retVal.length; i++ ) {
                                  if ( ( retVal[ i ] as React.ReactElement )?.type === 'br' ) {
                                      retVal.splice( i, 1 );
                                  }
                              }
                          }
                      }

                      return retVal;
                  },
                  [
                      errorMessage,
                      helpText,
                      infoTextLayout,
                      language,
                      messageCatalogTimestamp
                  ]
              ),
              infoTextTooltipContent                                    = useMemo(
                  () => {
                      const retVal : React.ReactNode[] = [];

                      if ( infoTextLayout !== 'text' ) {

                          // helpText
                          if ( helpText ) {
                              // TODO: message resolver for row item and so on
                              let messages : ( string | React.ReactNode | React.ReactNode[] )[] | undefined;

                              if ( typeof helpText === 'string' ) {
                                  messages = [ messageResolver( helpText ) ?? '' ];
                              }
                              else if ( !Array.isArray( helpText ) ) {
                                  messages = [ helpText ];
                              }
                              else {
                                  messages = helpText;
                              }

                              retVal.push(
                                  <div key="help" className="yn-tooltip-help">
                                      <div className="yn-tooltip-header">
                                          {__( 'YN.base.frontend.dataTable.tooltip.helpText.title' )}
                                      </div>
                                      {messages.map( ( message, index ) => <div key={`help_${index}`}
                                                                                className="yn-help-message">{message}</div> )}
                                  </div>
                              );
                          }

                          // error
                          if ( errorMessage ) {
                              let messages : ( string | React.ReactNode | React.ReactNode[] )[] | undefined;

                              if ( typeof errorMessage === 'string' ) {
                                  messages = [ __( errorMessage ) ];
                              }
                              else if ( !Array.isArray( errorMessage ) ) {
                                  messages = [ errorMessage ];
                              }
                              else {
                                  messages = errorMessage;
                              }

                              retVal.push(
                                  <div key="error" className="yn-tooltip-error">
                                      <div className="yn-tooltip-header">
                                          {__( 'YN.base.common.tooltip.error.title' )}
                                      </div>
                                      {messages.map( ( message, index ) => <div key={`error_${index}`}
                                                                                className="yn-error-message">{message}</div> )}
                                  </div>
                              );
                          }

                          // inheritanceTooltip
                          if ( resolvedInheritanceTooltip && infoTextLayout === 'hidden' ) {
                              if ( retVal.length ) {
                                  retVal.push( <br/> );
                              }

                              retVal.push( resolvedInheritanceTooltip );
                          }
                      }

                      return retVal;
                  },
                  [
                      errorMessage,
                      helpText,
                      infoTextLayout,
                      language,
                      messageCatalogTimestamp,
                      resolvedInheritanceTooltip
                  ]
              ),
              classNamesToApply                                         = useMemo(
                  () => {
                      const retVal = ( defaultProps.classNames.concat( classNames ) );

                      // visibility
                      retVal.push( `yn-visibility-${visibility}` );

                      // alignment
                      retVal.push( `yn-alignment-${alignment}` );

                      // labelAlignment
                      retVal.push( `yn-label-alignment-${labelAlignment}` );

                      // labelPosition
                      retVal.push( `yn-label-position-${labelPosition}` );

                      // labelVisibility
                      retVal.push( `yn-label-visibility-${labelVisibility}` );

                      // required
                      if ( required ) {
                          retVal.push( 'yn-field-required' );
                      }

                      // error
                      if ( errorMessage ) {
                          retVal.push( 'yn-error-border' );
                      }

                      // isDirty
                      if ( isDirty ) {
                          retVal.push( 'yn-dirty-border' );
                      }

                      if ( infoTextLayout === 'text' ) {
                          retVal.push( 'yn-info-text' );
                      }
                      else if ( infoTextLayout === 'icon' ) {
                          retVal.push( 'yn-info-icon' );
                      }
                      else {
                          retVal.push( 'yn-info-hidden' );
                      }

                      return retVal;
                  },
                  [
                      alignment,
                      classNames,
                      errorMessage,
                      infoTextLayout,
                      isDirty,
                      labelAlignment,
                      labelPosition,
                      labelVisibility,
                      required,
                      visibility
                  ]
              ),
              displayClassNamesToApply                                  = useMemo(
                  () => {
                      const retVal = defaultProps.displayClassNames.concat( displayClassNames );

                      // disabled
                      if ( disabled ) {
                          retVal.push( 'p-disabled' );
                      }

                      return retVal;
                  },
                  [
                      disabled,
                      displayClassNames
                  ]
              ),
              messageClassNamesToApply                                  = useMemo(
                  () => {
                      let retVal : string[] | undefined;

                      if ( infoTextLayout === 'text' ) {
                          retVal = defaultProps.messageClassNames.concat( messageClassNames );
                          retVal.push( 'yn-field-message-text' );
                      }

                      else if ( infoTextLayout === 'icon' ) {
                          retVal = [ 'yn-field-message-icon' ];

                          if ( errorMessage ) {
                              retVal.push( 'yn-error-icon pi pi-exclamation-circle' );
                          }
                          else if ( helpText ) {
                              retVal.push( 'yn-help-icon pi pi-question-circle' );
                          }
                          else {
                              retVal.push( 'yn-invisible-icon' );
                          }
                      }

                      return retVal;
                  },
                  [
                      errorMessage,
                      helpText,
                      infoTextLayout
                  ]
              ),
              labelStyleToApply                                         = useMemo(
                  () => {
                      const retVal = { ...defaultProps.labelStyle, ...labelStyle };

                      if ( labelPosition === 'left' || labelPosition === 'right' ) {
                          if ( !labelWidth || labelVisibility === 'hidden' ) {
                              if ( !labelWidth ) {
                                  retVal.display = 'none';
                              }
                          }
                          else {
                              retVal.width = `${labelWidth}px`;
                          }
                      }

                      return retVal;
                  },
                  [
                      labelPosition,
                      labelStyle,
                      labelVisibility,
                      labelWidth
                  ]
              ),
              displayStyleToApply                                       = useMemo(
                  () => {
                      const retVal = {
                          ...defaultProps.displayStyle,
                          ...displayStyle
                      };

                      const displayValueMarginRem  = .25, // left = right
                            displayValueMarginPx   = -1, // left = right
                            displayValuePaddingRem = .5, // left = right - no px
                            messageIconWidthRem    = 1.2, // no px
                            messageIconMarginRem   = .5; // left = right - no px

                      let widthRem  = 2 * displayValueMarginRem + 2 * displayValuePaddingRem + displayValueWidthOffsetRem,
                          widthPx   = 2 * displayValueMarginPx + displayValueWidthOffsetPx,
                          marginRem = displayValueMarginRem,
                          marginPx  = displayValueMarginPx;

                      if ( infoTextLayout === 'icon' ) {
                          widthRem += messageIconWidthRem + messageIconMarginRem;

                          if ( labelPosition === 'left' || labelPosition === 'right' ) {
                              widthRem += messageIconMarginRem;
                          }

                          if ( labelPosition !== 'right' && !( ( labelPosition === 'top' || labelPosition === 'bottom' ) && labelAlignment === 'right' ) ) {
                              marginRem += messageIconWidthRem + messageIconMarginRem;

                              if ( labelPosition === 'left' ) {
                                  marginRem += messageIconMarginRem;
                              }
                          }
                      }

                      if ( labelPosition === 'left' || labelPosition === 'right' ) {
                          if ( labelWidth && labelVisibility !== 'hidden' ) {
                              widthPx += labelWidth;

                              if ( labelPosition === 'left' ) {
                                  marginPx += labelWidth;
                              }
                          }
                      }

                      retVal.width = `calc( 100% - ${widthRem}rem - ${widthPx}px )`;
                      retVal.marginLeft = `calc( ${marginRem}rem + ${marginPx}px )`;

                      return retVal;
                  },
                  [
                      displayStyle,
                      infoTextLayout,
                      labelPosition,
                      labelVisibility,
                      labelWidth
                  ]
              ),
              textMessageStyleToApply                                   = useMemo(
                  () => {
                      const retVal = { ...defaultProps.messageStyle, ...messageStyle };

                      if ( ( labelPosition === 'left' || labelPosition === 'right' ) && labelWidth && labelVisibility !== 'hidden' ) {
                          retVal.width = `calc( 100% - ${labelWidth}px )`;

                          if ( labelPosition === 'left' ) {
                              retVal.marginLeft = `${labelWidth}px`;
                          }
                      }

                      return retVal;
                  },
                  [
                      labelPosition,
                      labelVisibility,
                      labelWidth,
                      messageStyle
                  ]
              ),
              autoCompleteStyleToApply : React.CSSProperties            = useMemo(
                  () => {
                      return {
                          width      : displayStyleToApply.width,
                          marginLeft : displayStyleToApply.marginLeft,
                          marginRight: displayStyleToApply.marginRight
                      };
                  },
                  [
                      displayStyleToApply.width,
                      displayStyleToApply.marginLeft,
                      displayStyleToApply.marginRight
                  ]
              );

        // resolve appendTo
        useLayoutEffect(
            () => {
                if ( appendToElementId && appendToElementId !== 'self' ) {
                    let appendToElement : HTMLElement | null;

                    if ( appendToElementId !== 'document' ) {
                        appendToElement = document.getElementById( appendToElementId );
                    }
                    else {
                        appendToElement = document.body;
                    }

                    if ( appendToElement && appendToElement !== appendTo ) {
                        setAppendTo( appendToElement );
                    }
                }
            }
        );

        // realign overlay
        useLayoutEffect(
            () => {
                const overlayElement : HTMLElement | undefined = autoCompleteRef.current?.getOverlay(),
                      targetElement : HTMLElement | undefined  = autoCompleteRef.current?.getElement();

                if ( overlayElement && targetElement ) {
                    // wrong TypeScript definition for DomHandler.alignOverlay
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    DomHandler.alignOverlay( overlayElement, targetElement, appendTo ?? PrimeReact.appendTo );

                    if ( overlayElement.style.transformOrigin.indexOf( 'bottom' ) !== -1 ) {
                        overlayElement.style.marginTop = '-2px';
                    }
                    else {
                        overlayElement.style.marginTop = '2px';
                    }
                }
            }
        );

        // realign and show/hide tooltips
        useEffect(
            () => {
                const overlayElement : HTMLElement | undefined = autoCompleteRef.current?.getOverlay();

                if ( tooltipDisabled || overlayElement ) {
                    infoTextTooltipElement.current?.hide();
                    tooltipElement.current?.hide();
                }
                else {
                    const tooltipEl = infoTextTooltipElement.current?.getElement() ?? tooltipElement.current?.getElement();

                    if ( tooltipEl ) {
                        tooltipEl.style.width = '';
                        tooltipEl.style.height = '';

                        const tooltipElBox = tooltipEl.getBoundingClientRect();

                        if ( tooltipElBox ) {
                            tooltipEl.style.width = `${tooltipElBox.width}px`;
                            tooltipEl.style.height = `${tooltipElBox.height}px`;

                            if ( tooltipElBox.x < 12 ) {
                                tooltipEl.style.left = '12px';
                            }
                            else if ( tooltipElBox.x + tooltipElBox.width > document.body.clientWidth - 12 ) {
                                tooltipEl.style.left = `${document.body.clientWidth - tooltipElBox.width - 12}px`;
                            }
                            if ( tooltipElBox.y < 12 ) {
                                tooltipEl.style.top = '12px';
                            }
                            else if ( tooltipElBox.y + tooltipElBox.height > document.body.clientHeight - 12 ) {
                                tooltipEl.style.top = `${document.body.clientHeight - tooltipElBox.height - 12}px`;
                            }
                        }
                    }
                }
            },
            [
                tooltipActive
            ]
        );

        // update auto complete value
        useEffect(
            () => {
                if ( autoCompleteRef.current?.getInput() ) {
                    let hasFocus = autoCompleteInputHasFocus;

                    if ( hasFocus === undefined ) {
                        const wrapper = document.getElementById( `${uid}_fieldWrapper` );

                        let focused = document.activeElement;

                        if ( !focused || focused === document.body ) {
                            focused = null;
                        }
                        else if ( document.querySelector ) {
                            focused = document.querySelector( ':focus' );
                        }

                        hasFocus = wrapper?.contains( focused );
                    }

                    if ( hasFocus ) {
                        const valueToSet = autoCompleteValue ?? '';

                        // search if value changed
                        if ( appliedAutoCompleteValue !== valueToSet ) {
                            // set value directly to input (workaround for React)
                            const nativeInputValueSetter = Object.getOwnPropertyDescriptor( window.HTMLInputElement.prototype, 'value' )?.set;
                            nativeInputValueSetter?.call( autoCompleteRef.current.getInput(), valueToSet );

                            // trigger change event
                            autoCompleteRef.current.getInput().dispatchEvent( new Event( 'change', { bubbles: true, cancelable: true } ) );
                        }

                        // search if autoCompleteMinLength === 0 and no value
                        if ( autoCompleteMinLength === 0 && valueToSet === '' ) {
                            autoCompleteRef.current.search( createSyntheticEvent( new Event( 'build' ) ), '', 'dropdown' );
                        }
                    }
                }
            },
            [
                appliedAutoCompleteValue,
                autoCompleteValue,
                autoCompleteInputHasFocus
            ]
        );

        // 2rem;
        return (
            <div
                {...pt?.root}
                id={`${uid}_fieldWrapper`}
                className={classNamesToApply.join( ' ' )}
                style={styleToApply}
                onKeyDown={autoComplete && !disabled ? onKeyDown : undefined}
                onMouseDown={autoComplete && !disabled ? onMouseDown : undefined}
                onContextMenu={!disabled ? onContextMenu : undefined}
            >
                {children}
                {displayValue !== undefined &&
                    <div {...pt?.displayValue} className={displayClassNamesToApply.join( ' ' )} style={displayStyleToApply}>
                        <div className="yn-field-displayvalue-bg"></div>
                        {( typeof displayValue !== 'string' || displayValue.trim() !== '' ) &&
                            <div className="yn-field-displayvalue-content">{displayValue}</div>
                        }
                        {( typeof displayValue === 'string' && displayValue.trim() === '' ) &&
                            <div className="yn-field-displayvalue-content">&nbsp;</div>
                        }
                    </div>
                }
                {autoCompleteComp !== undefined &&
                    <div className="yn-field-autocomplete" style={autoCompleteStyleToApply}>
                        {autoCompleteComp}
                    </div>
                }
                <label {...pt?.label} htmlFor={uid} className={labelClassNamesToApply.join( ' ' )}
                       style={labelStyleToApply}>{resolvedLabel}</label>
                {infoTextLayout === 'text' &&
                    <small {...pt?.infoText} id={`${uid}_message`} className={messageClassNamesToApply?.join( ' ' )}
                           style={textMessageStyleToApply}>{infoTextMessage.join( ' ' )}</small>
                }
                {infoTextLayout === 'icon' &&
                    <small {...pt?.infoText} id={`${uid}_message`} className={messageClassNamesToApply?.join( ' ' )}/>
                }
                {infoTextLayout !== 'text' && infoTextTooltipContent.length > 0 &&
                    <Tooltip
                        ref={infoTextTooltipElement}
                        {...pt?.infoTextTooltip}
                        target={`#${uid}${infoTextLayout === 'icon' ? '_message' : ''}`}
                        position="top"
                        className="yn-tooltip"
                        disabled={tooltipDisabled || overlay}
                        autoHide={false}
                        onShow={() => {
                            setTooltipActive( true );
                        }}
                        onHide={() => {
                            setTooltipActive( false );
                        }}
                    >
                        {infoTextTooltipContent}
                    </Tooltip>
                }
                {( ( tooltip && ( infoTextLayout !== 'hidden' || !infoTextTooltipContent.length ) ) || ( resolvedInheritanceTooltip && infoTextLayout !== 'hidden' ) ) &&
                    <Tooltip
                        ref={tooltipElement}
                        {...pt?.tooltip}
                        target={`#${uid}`}
                        position="top"
                        {...tooltipOptions}
                        disabled={tooltipDisabled || overlay}
                        autoHide={false}
                        onShow={() => {
                            setTooltipActive( true );
                        }}
                        onHide={() => {
                            setTooltipActive( false );
                        }}
                    >
                        {resolvedTooltip}
                        {resolvedTooltip && resolvedInheritanceTooltip &&
                            <br/>
                        }
                        {resolvedInheritanceTooltip}
                    </Tooltip>
                }
            </div>
        );
    }
}
FieldWrapper.defaultProps = defaultProps;

export default FieldWrapper;
